"use strict";
/*!
 * React Canvas Resize <https://github.com/smujmaiku/react-canvas-resize>
 * Copyright(c) 2020 Michael Szmadzinski
 * MIT Licensed
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Canvas = exports.useContainBox = exports.useAnimationFrame = exports.containBox = void 0;
const react_1 = __importStar(require("react"));
/**
 * Reduces a box inside a container
 * smujmaiku/moremath-js<https://github.com/smujmaiku/moremath-js>
 */
function containBox(box, container) {
    if (container.length < 2)
        return container;
    const scales = container.map((v, i) => v / box[i]);
    const scale = scales.reduce((a, b) => Math.min(a, b));
    return box.map(v => v * scale);
}
exports.containBox = containBox;
;
/**
 * Animation Frame Hook
 */
function useAnimationFrame(fn) {
    react_1.useEffect(() => {
        let timer;
        let count = 0;
        const frameTimes = [];
        const handleFrame = () => {
            const now = Date.now();
            const interval = now - frameTimes[frameTimes.length - 1];
            frameTimes.push(now);
            while (frameTimes.length > 2 && frameTimes[0] < now - 1000) {
                frameTimes.shift();
            }
            const minFrameTime = Math.min(frameTimes[0], now - 500);
            const fps = Math.round((frameTimes.length - 1) / (now - minFrameTime) * 1000);
            cancelAnimationFrame(timer);
            timer = requestAnimationFrame(handleFrame);
            try {
                fn({ count, now, interval, fps });
                count++;
            }
            catch (e) {
                frameTimes.pop();
            }
        };
        handleFrame();
        return () => {
            cancelAnimationFrame(timer);
        };
    }, [fn]);
}
exports.useAnimationFrame = useAnimationFrame;
;
/**
 * Contained box based on ref hook
 */
function useContainBox(ref, ratio) {
    let ratioX = 1;
    let ratioY = 1;
    if (typeof ratio === 'number') {
        ratioX = ratio;
    }
    else if (typeof ratio === 'string') {
        [ratioX = 1, ratioY = 1] = ratio.split(/[x:\/]/).map((v) => parseInt(v));
    }
    else if (ratio instanceof Array) {
        [ratioX = 1, ratioY = 1] = ratio;
    }
    const [box, setBox] = react_1.useState({
        left: 0,
        top: 0,
        width: 1,
        height: 1,
        fullWidth: 1,
        fullHeight: 1,
        scale: 1,
    });
    const checkResize = react_1.useCallback(() => {
        const root = ref.current;
        if (!root)
            return;
        const { offsetWidth, offsetHeight } = root;
        const newBox = {
            left: 0,
            top: 0,
            width: offsetWidth,
            height: offsetHeight,
            fullWidth: offsetWidth,
            fullHeight: offsetHeight,
            scale: 1,
        };
        [newBox.width, newBox.height] = containBox([ratioX, ratioY], [offsetWidth, offsetHeight]);
        newBox.left = Math.floor((offsetWidth - newBox.width) / 2);
        newBox.top = Math.floor((offsetHeight - newBox.height) / 2);
        newBox.width = Math.floor(newBox.width);
        newBox.height = Math.floor(newBox.height);
        newBox.scale = newBox.width / ratioX;
        setBox((orig) => {
            if (newBox.width === orig.width && newBox.height === orig.height &&
                newBox.left === orig.left && newBox.top === orig.top) {
                return orig;
            }
            return newBox;
        });
    }, [ratioX, ratioY, ref]);
    useAnimationFrame(checkResize);
    return box;
}
exports.useContainBox = useContainBox;
/**
 * Canvas React Element
 */
function Canvas(props) {
    const { width, height, onInit, onDraw } = props, otherProps = __rest(props, ["width", "height", "onInit", "onDraw"]);
    const canvasRef = react_1.useRef(null);
    const drawCanvas = react_1.useCallback((opts) => {
        const { count } = opts;
        const canvas = canvasRef.current;
        if (!canvas) {
            throw new Error('Canvas is not ready');
        }
        if (count === 0 && onInit) {
            onInit(canvas);
        }
        if (onDraw) {
            onDraw(Object.assign({ canvas }, opts));
        }
    }, [canvasRef, onInit, onDraw]);
    useAnimationFrame(drawCanvas);
    return react_1.default.createElement("canvas", Object.assign({}, otherProps, { ref: canvasRef, width: width, height: height }));
}
exports.Canvas = Canvas;
;
/**
 * Resizing Canvas React Element
 */
function CanvasResize(props) {
    const { canvasProps = {}, ratio, onInit, onDraw, onResize, fillCanvas, style = {} } = props, otherProps = __rest(props, ["canvasProps", "ratio", "onInit", "onDraw", "onResize", "fillCanvas", "style"]);
    const rootRef = react_1.useRef(null);
    const box = useContainBox(rootRef, ratio);
    react_1.useEffect(() => {
        if (!onResize)
            return;
        onResize(box);
    }, [box, onResize]);
    const handleDraw = react_1.useCallback((frame) => {
        onDraw(Object.assign(Object.assign({}, frame), { box }));
    }, [box]);
    const width = fillCanvas ? box.fullWidth : box.width;
    const height = fillCanvas ? box.fullHeight : box.height;
    const left = fillCanvas ? 0 : box.left;
    const top = fillCanvas ? 0 : box.top;
    return react_1.default.createElement("div", Object.assign({}, otherProps, { ref: rootRef, style: Object.assign(Object.assign({}, style), { padding: 0, overflow: 'hidden' }) }),
        react_1.default.createElement(Canvas, Object.assign({}, canvasProps, { style: Object.assign(Object.assign({}, (canvasProps.style || {})), { margin: 0, marginLeft: left, marginTop: top }), width: width, height: height, onInit: onInit, onDraw: handleDraw })));
}
exports.default = CanvasResize;
